import * as THREE from 'three';
import * as marietta from '../../..';
import { Dialog } from '../../../core/view';
import { ItemScale } from '../../../view3d/webcad/ItemScale';
import {
  MIN_HEIGHT_EDITABLE_ITEM_PLAN_SIZE_MM, MIN_INSTALLATION_HEIGHT_MM, MIN_ITEM_HEIGHT_MM, MM_PER_M,
} from '../../Constants';

const WIDTH_INPUT_ID: string = 'itemSizeSettingWidth';
const DEPTH_INPUT_ID: string = 'itemSizeSettingDepth';
const HEIGHT_INPUT_ID: string = 'itemSizeSettingHeight';
const INSTALLATION_HEIGHT_INPUT_ID: string = 'itemSizeSettingInstallationHeight';
const INSTALLATION_HEIGHT_ROW_ID: string = 'itemSizeSettingInstallationHeightRow';
// 高さ編集の対象外（天井アイテム・エクステリア）の幅・奥行・高さの下限(mm)。従来の値を保つ
const MIN_OTHER_ITEM_SIZE_MM: number = 10;

/**
 * 3Dーアイテムのサイズ設定
 * 対象：家具、設備、エクステリア
 */
export default class ItemSizeSettingDialog extends Dialog {
  public target: any = undefined;

  openCallback: Function = undefined;

  closeCallback: Function = undefined;

  // public item: marietta.webcad.item.MonicaObject = null
  /**
   * コンストラクタ
   */
  // eslint-disable-next-line no-useless-constructor
  constructor(draggable: boolean, modal: boolean) {
    super(draggable, modal);
  }

  buildContent() {
    // ダイアログのコンテンツ生成
    const id = 'dialogSizeSetting';

    let title = '';
    // タイトル
    switch (marietta.webcad.WebCADGlobal.language) {
      case 'jp':
        title = 'サイズ詳細設定';
        break;
      default:
        title = 'Size Setting';
        break;
    }

    let contents = '';
    contents += '<div class="bodyContents">';
    contents += `<img src="${marietta.webcad.WebCADGlobal.webcad2Dmanager.assets_url}item_size_setting.png" height="80"/><div class="currentFrame"></div></button>`;
    contents += '<table class="sizeSettingTable">';
    contents += '<tbody>';
    contents += ' <tr>';
    contents += '<td><span class="jp">幅</span><span class="en">Width</span></td>';
    contents += `<td><input id="${WIDTH_INPUT_ID}" type="number" min="${MIN_OTHER_ITEM_SIZE_MM}" max="99999"><span class="unit">mm</span></td>`;
    contents += '</tr>';
    contents += ' <tr>';
    contents += '<td><span class="jp">奥行</span><span class="en">Depth</span></td>';
    contents += `<td><input id="${DEPTH_INPUT_ID}" type="number" min="${MIN_OTHER_ITEM_SIZE_MM}" max="99999"><span class="unit">mm</span></td>`;
    contents += '</tr>';
    contents += ' <tr>';
    contents += '<td><span class="jp">高さ</span><span class="en">Height</span></td>';
    contents += `<td><input id="${HEIGHT_INPUT_ID}" type="number" min="${MIN_OTHER_ITEM_SIZE_MM}" max="99999"><span class="unit">mm</span></td>`;
    contents += '</tr>';
    contents += `<tr id="${INSTALLATION_HEIGHT_ROW_ID}">`;
    contents += '<td><span class="jp">設置高さ</span><span class="en">Installation height</span></td>';
    contents += `<td><input id="${INSTALLATION_HEIGHT_INPUT_ID}" type="number" min="${MIN_INSTALLATION_HEIGHT_MM}" max="99999"><span class="unit">mm</span></td>`;
    contents += '</tr>';
    contents += '</tbody>';
    contents += '</table>';
    contents += '</div>';

    let footer = '<button id="btnSizeSettingDone"><span class="jp">ＯＫ</span><span class="en">OK</span></button>';
    footer += '<button id="btnSizeSettingClose"><span class="jp">キャンセル</span><span class="en">Cancel</span></button>';

    this.createDialog(id, title, contents, footer);

    // 初期値の設定
    this.init();
    // イベントの設定
    this.setEvent();

    if (this.openCallback != null) {
      this.openCallback();
    }
  }

  /**
   * アイテム変更イベントの生成
   */
  setEvent() {
    document.getElementById('btnSizeSettingDone')
      .addEventListener('click', this.clickOKBtn);
    document.getElementById('btnSizeSettingClose')
      .addEventListener('click', this.close);
    // 入力された内容を制限する
    document.querySelectorAll('#dialogSizeSetting .sizeSettingTable td input')
      .forEach((element) => {
        // 整数の mm だけを入力できるようにする（「-」や小数点を打てないように）
        marietta.webcad.util.InputValidationUtil.setIntegerMmFilter(element as HTMLInputElement);
        element.addEventListener('change', (e) => {
          const target = (e.target as HTMLInputElement);
          const min = this.getMinValue(target.id);
          if (Number(target.value) > marietta.webcad.WebCADGlobal.MAX_SIZE_CHANGE_MONICA) {
            target.value = marietta.webcad.WebCADGlobal.MAX_SIZE_CHANGE_MONICA.toString();
          } else if (Number(target.value) < min) {
            target.value = min.toString();
          }
        });
      });
  }

  // OKボタン押下
  clickOKBtn = () => {
    const width = Number((document.getElementById(WIDTH_INPUT_ID) as HTMLInputElement).value);
    const depth = Number((document.getElementById(DEPTH_INPUT_ID) as HTMLInputElement).value);
    const height = Number((document.getElementById(HEIGHT_INPUT_ID) as HTMLInputElement).value);
    console.log(width, depth, height);
    const targetItem = this.getTargetItem();
    // OK で設置高さ欄の値を z に書く。CADAPI が積み上げた高さもここで固定値になる
    if (targetItem && targetItem.isHeightEditableItem()) {
      const installationHeight = Number((document.getElementById(INSTALLATION_HEIGHT_INPUT_ID) as HTMLInputElement).value);
      const { floorHeight } = marietta.webcad.WebCADGlobal.itemManager.getFloorAndCeilingHeightOfFloor(targetItem.itemInfo.floor);
      new ItemScale(this.target).setScaleAndHeight(width, depth, height, floorHeight + installationHeight);
    } else {
      new ItemScale(this.target).setScale(width, depth, height);
    }
    this.close();
  };

  // 初期値の設定
  init = () => {
    const { obb } = this.target.userData.obb;
    const size = obb.getSize(new THREE.Vector3());
    const { scale } = this.target;

    (document.getElementById(WIDTH_INPUT_ID) as HTMLInputElement).value = String(Math.round(Math.abs(size.x * scale.x * 1000)));
    (document.getElementById(DEPTH_INPUT_ID) as HTMLInputElement).value = String(Math.round(Math.abs(size.z * scale.z * 1000)));
    (document.getElementById(HEIGHT_INPUT_ID) as HTMLInputElement).value = String(Math.round(Math.abs(size.y * scale.y * 1000)));

    const targetItem = this.getTargetItem();
    const isHeightEditable = this.isHeightEditableTarget();
    document.getElementById(INSTALLATION_HEIGHT_ROW_ID).style.display = isHeightEditable ? '' : 'none';
    [WIDTH_INPUT_ID, DEPTH_INPUT_ID, HEIGHT_INPUT_ID].forEach((inputId) => {
      (document.getElementById(inputId) as HTMLInputElement).min = this.getMinValue(inputId).toString();
    });
    if (!targetItem) return;
    if (isHeightEditable) {
      (document.getElementById(INSTALLATION_HEIGHT_INPUT_ID) as HTMLInputElement).value = String(this.getSceneInstallationHeight(targetItem));
    }

    if (!targetItem.itemInfo.isWhiteItem()) {
      (document.getElementById(WIDTH_INPUT_ID) as HTMLInputElement).disabled = true;
      (document.getElementById(DEPTH_INPUT_ID) as HTMLInputElement).disabled = true;
      (document.getElementById(HEIGHT_INPUT_ID) as HTMLInputElement).disabled = true;
    }
  };

  /**
   * 入力欄ごとの下限(mm)。高さ編集の対象（住設・家具）は幅・奥行・高さとも 1mm
   */
  private getMinValue(inputId: string): number {
    switch (inputId) {
      case INSTALLATION_HEIGHT_INPUT_ID:
        return MIN_INSTALLATION_HEIGHT_MM;
      case HEIGHT_INPUT_ID:
        return this.isHeightEditableTarget() ? MIN_ITEM_HEIGHT_MM : MIN_OTHER_ITEM_SIZE_MM;
      default:
        return this.isHeightEditableTarget() ? MIN_HEIGHT_EDITABLE_ITEM_PLAN_SIZE_MM : MIN_OTHER_ITEM_SIZE_MM;
    }
  }

  /**
   * 3D の実位置から設置高さ(mm)を出す。z 未設定でも CADAPI が積み上げた分（台の上など）が入る
   */
  private getSceneInstallationHeight(targetItem: marietta.webcad.item.MonicaObject): number {
    const worldHeightMm = this.target.getWorldPosition(new THREE.Vector3()).y * MM_PER_M;
    const { floorHeight } = marietta.webcad.WebCADGlobal.itemManager.getFloorAndCeilingHeightOfFloor(targetItem.itemInfo.floor);
    return Math.max(MIN_INSTALLATION_HEIGHT_MM, Math.round(worldHeightMm - floorHeight));
  }

  private isHeightEditableTarget(): boolean {
    const targetItem = this.getTargetItem();
    return !!targetItem && targetItem.isHeightEditableItem();
  }

  private getTargetItem(): marietta.webcad.item.MonicaObject {
    return marietta.webcad.WebCADGlobal.getItemByGuid(this.target.userData.modelInfo.uuid, this.target.userData.modelInfo.code);
  }

  // ダイアログを閉じる
  close = () => {
    // if (this.callBack) this.callBack();
    // ThreeView.get().unSelectedObject();
    if (this.closeCallback != null) {
      this.closeCallback();
    }

    this.closeEvent();
  };

  /**
   * override
   */
  closeEvent() {
    super.closeEvent();
  }
}
